import { useRef, useState } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { HeatmapOverlay } from "../../components/basketball/HeatmapOverlay.tsx";
import { AnimatedHeadline } from "../../components/common/AnimatedHeadline.tsx";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./CourtAnalytics.module.css";

interface Session {
  id: string;
  date: string;
  fg: number;
  zones: string[];
  topZones: string[];
}

const SESSIONS: Session[] = [
  {
    id: "s1",
    date: "Mar 15",
    fg: 62,
    zones: ["paint", "mid-range"],
    topZones: ["paint-left", "mid-left-wing", "above-break-center"],
  },
  {
    id: "s2",
    date: "Mar 13",
    fg: 55,
    zones: ["restricted-area", "mid-range"],
    topZones: ["restricted-area-left", "mid-left-elbow", "corner-three-left"],
  },
  {
    id: "s3",
    date: "Mar 11",
    fg: 48,
    zones: ["three-point"],
    topZones: ["above-break-left", "above-break-center", "mid-right-elbow"],
  },
  {
    id: "s4",
    date: "Mar 9",
    fg: 71,
    zones: ["paint", "restricted-area"],
    topZones: ["paint-left", "restricted-area-left", "mid-left-elbow"],
  },
  {
    id: "s5",
    date: "Mar 7",
    fg: 58,
    zones: ["three-point", "mid-range"],
    topZones: ["corner-three-right", "above-break-right", "mid-left-wing"],
  },
];

/** Build group labels from i18n - used inside the component */

/** Generate mock FG counts for a session's top zones */
function buildFgCounts(session: Session): Record<string, number> {
  const counts: Record<string, number> = {};
  const baseCounts = [8, 5, 3];
  for (let i = 0; i < session.topZones.length; i++) {
    counts[session.topZones[i]] = baseCounts[i] ?? 2;
  }
  return counts;
}

export function CourtAnalytics() {
  const { t } = useI18n();

  const GROUP_LABELS: Record<string, string> = {
    "restricted-area": t.classic.courtGroupRestricted,
    paint: t.classic.courtGroupPaint,
    "mid-range": t.classic.courtGroupMidRange,
    "three-point": t.classic.courtGroup3pt,
    deep: t.classic.courtGroupDeep,
  };

  const isMobile = useIsMobile();
  const [activeSessionIndex, setActiveSessionIndex] = useState(-1);
  const sessionCardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const activeSession = activeSessionIndex >= 0 ? SESSIONS[activeSessionIndex] : undefined;
  const activeZoneIds = activeSession?.topZones ?? [];
  const zoneFgCounts = activeSession ? buildFgCounts(activeSession) : undefined;

  const ref = useGsapScrollTrigger(
    (el) => {
      const cards = el.querySelectorAll("[data-session-card]");
      const courtSvg = el.querySelector("[data-court-svg]");

      if (!courtSvg || cards.length === 0) return;

      if (isMobile) {
        // Mobile: simple stagger fade-up, no pin
        gsap.from(courtSvg, {
          y: 20,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
        gsap.from(cards, {
          y: 30,
          opacity: 0,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
        // Show all sessions on mobile (no scroll-driven highlight)
        setActiveSessionIndex(0);
        return;
      }

      // Desktop: pinned scrub animation
      // Set initial state for session cards
      gsap.set(cards, { opacity: 0.3, scale: 0.95, y: 20 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          pin: true,
          scrub: 1,
          start: "top top",
          end: `+=${SESSIONS.length * 100}%`,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const progress = self.progress;
            const totalSessions = SESSIONS.length;
            const segmentSize = 1 / (totalSessions + 1);
            // First segment is the intro, then one segment per session
            const sessionProgress = (progress - segmentSize) / (1 - segmentSize);
            if (sessionProgress < 0) {
              setActiveSessionIndex(-1);
            } else {
              const idx = Math.min(Math.floor(sessionProgress * totalSessions), totalSessions - 1);
              setActiveSessionIndex(idx);
            }
          },
        },
      });

      // Phase 1: Intro - court fades in
      tl.to(courtSvg, { opacity: 1, duration: 1 });

      // Phase 2-6: Each session highlights
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        // Bring card in
        tl.to(card, {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.5,
        });
        // Hold
        tl.to(card, { duration: 0.5 });
        // Fade out if not the last card
        if (i < cards.length - 1) {
          tl.to(card, {
            opacity: 0.3,
            scale: 0.95,
            duration: 0.3,
          });
        }
      }
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.heading}>
        <AnimatedHeadline text={t.classic.courtTitle} as="h2" className={styles.title} />
        <p className={styles.description}>{t.classic.courtDesc}</p>
      </div>

      <div className={styles.splitLayout}>
        <div className={styles.courtPanel} data-court-svg>
          <HeatmapOverlay
            className={styles.courtSvg}
            activeZoneIds={activeZoneIds}
            zoneFgCounts={zoneFgCounts}
          />
          {activeSession && (
            <div className={styles.courtMeta}>
              <span className={styles.courtMetaFg}>
                {t.classic.courtFgLabel} {activeSession.fg}%
              </span>
              <span className={styles.courtMetaDate}>{activeSession.date}</span>
            </div>
          )}
        </div>

        <div className={styles.sessionPanel}>
          <h3 className={styles.sessionPanelTitle}>{t.classic.courtRecentSessions}</h3>
          <div className={styles.sessionList}>
            {SESSIONS.map((session, idx) => (
              <div
                key={session.id}
                ref={(el) => {
                  sessionCardsRef.current[idx] = el;
                }}
                className={`${styles.sessionCard} ${
                  activeSessionIndex === idx ? styles.sessionCardActive : ""
                }`}
                data-session-card
              >
                <div className={styles.sessionCardHeader}>
                  <span className={styles.sessionDate}>{session.date}</span>
                  <span className={styles.sessionFg}>{session.fg}%</span>
                </div>
                <div className={styles.sessionZones}>
                  {session.zones.map((zone) => (
                    <span key={zone} className={styles.sessionZoneBadge}>
                      {GROUP_LABELS[zone] ?? zone}
                    </span>
                  ))}
                </div>
                <div className={styles.sessionTopZones}>
                  {session.topZones.map((zoneId) => (
                    <span key={zoneId} className={styles.sessionTopZoneDot}>
                      {zoneId
                        .split("-")
                        .map((w) => w[0]?.toUpperCase())
                        .join("")}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
